import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Spinner,
  Alert,
  Chip,
  Tabs,
  EmptyState,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { DANGER_BTN, NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  AdminApiError,
  type RecallListItem,
  type RecallDetail,
  type RecallStatusFilter,
} from '../lib/adminApiClient';

/**
 * Recall console (recall:edit).
 * - List active / closed recalls (USDA FSIS + FDA openFDA, ingested daily by the
 *   recallSync bg-job).
 * - Curate the products each recall affects: link / unlink by GLPC handle.
 * - Close a recall (archive + stop showing it to consumers) or reopen it.
 * Server is the source of truth — every action re-checks the capability and is
 * audited (AdminAuditLog). Products are referenced by GLPC + name only; UPC is
 * NEVER shown (feedback_never_show_upc).
 */

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : '—';

const classColor = (c: string | null): 'error' | 'warning' | 'default' =>
  c === 'Class I' ? 'error' : c === 'Class II' ? 'warning' : 'default';

export default function RecallConsole() {
  const caps = useCapabilities();
  const canView = caps.has('recall:read');
  const canEdit = caps.has('recall:edit');

  const [status, setStatus] = useState<RecallStatusFilter>('active');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<RecallListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listRecalls({
        status,
        q: search || undefined,
        limit: 50,
      });
      setItems(res.items);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recalls');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await adminApiClient.listRecalls({
        status,
        q: search || undefined,
        before: nextCursor,
        limit: 50,
      });
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof Error ? err.message : 'Failed to load more' });
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, status, search]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const onCardChanged = (kind: 'success' | 'error', msg: string, updated?: RecallListItem) => {
    setFlash({ kind, msg });
    if (updated) {
      setItems((prev) => prev.map((r) => (r.recallId === updated.recallId ? updated : r)));
    }
  };

  if (caps.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (!canView) {
    return <Alert severity="error">You don’t have permission to view recalls.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Recall console
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Curate the products each food recall affects and open or close recalls. Every change is
        audited.
      </Typography>

      {!canEdit && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse recalls but can’t make changes.</Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Tabs
          ariaLabel="Recall status"
          value={status}
          onChange={(_e, v) => setStatus(v as RecallStatusFilter)}
          tabs={[
            { value: 'active', label: 'Active' },
            { value: 'closed', label: 'Closed' },
            { value: 'all', label: 'All' },
          ]}
        />
        <Box sx={{ flex: 1 }} />
        <TextField
          label="Search title"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          size="small"
          sx={{ width: 240 }}
        />
        <Button
          variant="outlined"
          sx={NEUTRAL_BTN}
          ariaLabel="Apply title search"
          buttonText="Search"
          onClick={() => setSearch(searchDraft.trim())}
        />
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind === 'success' ? 'success' : 'error'}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState icon="warning" title="No recalls" body="No recalls match this filter." />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((r) => (
              <RecallCard key={r.recallId} recall={r} canWrite={canEdit} onChanged={onCardChanged} />
            ))}
          </Box>
          {nextCursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                sx={NEUTRAL_BTN}
                ariaLabel="Load more recalls"
                buttonText={loadingMore ? 'Loading…' : 'Load more'}
                disabled={loadingMore}
                onClick={() => void loadMore()}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function RecallCard({
  recall,
  canWrite,
  onChanged,
}: {
  recall: RecallListItem;
  canWrite: boolean;
  onChanged: (kind: 'success' | 'error', msg: string, updated?: RecallListItem) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<RecallDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');

  const isClosed = recall.status === 'closed';
  const activeLinks = detail?.links.filter((l) => l.isActive) ?? [];

  const loadDetail = useCallback(async () => {
    setDetailLoading(true);
    try {
      setDetail(await adminApiClient.getRecall(recall.recallId));
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to load links');
    } finally {
      setDetailLoading(false);
    }
  }, [recall.recallId, onChanged]);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) void loadDetail();
  };

  const toggleClosed = async () => {
    setBusy(true);
    try {
      if (isClosed) {
        await adminApiClient.reopenRecall(recall.recallId);
        onChanged('success', `Reopened “${recall.title}”.`, { ...recall, status: 'active' });
      } else {
        await adminApiClient.closeRecall(recall.recallId);
        onChanged('success', `Closed “${recall.title}”.`, {
          ...recall,
          status: 'closed',
          linkedProductCount: 0,
        });
      }
      if (expanded) await loadDetail();
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to update recall');
    } finally {
      setBusy(false);
    }
  };

  const linkProduct = async () => {
    const glpProductId = linkDraft.trim();
    if (!glpProductId) return;
    setBusy(true);
    try {
      await adminApiClient.linkRecallProduct(recall.recallId, glpProductId);
      setLinkDraft('');
      await loadDetail();
      onChanged('success', `Linked ${glpProductId}.`, {
        ...recall,
        linkedProductCount: recall.linkedProductCount + 1,
      });
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to link product');
    } finally {
      setBusy(false);
    }
  };

  const unlinkProduct = async (glpProductId: string) => {
    setBusy(true);
    try {
      await adminApiClient.unlinkRecallProduct(recall.recallId, glpProductId);
      onChanged('success', `Unlinked ${glpProductId}.`, {
        ...recall,
        linkedProductCount: Math.max(0, recall.linkedProductCount - 1),
      });
      await loadDetail();
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to unlink product');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {recall.title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {recall.source}
            {recall.recallNumber ? ` · #${recall.recallNumber}` : ''} · {fmtDate(recall.recallDate)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={isClosed ? 'Closed' : 'Active'}
              color={isClosed ? 'default' : 'success'}
              size="small"
            />
            {recall.classification && (
              <Chip
                label={recall.classification}
                color={classColor(recall.classification)}
                size="small"
              />
            )}
            <Chip
              label={`${recall.linkedProductCount} product${recall.linkedProductCount === 1 ? '' : 's'}`}
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
          {canWrite && (
            <Button
              variant="contained"
              color={isClosed ? 'primary' : 'error'}
              ariaLabel={isClosed ? 'Reopen recall' : 'Close recall'}
              buttonText={isClosed ? 'Reopen' : 'Close'}
              disabled={busy}
              onClick={() => void toggleClosed()}
            />
          )}
          <Button
            variant="outlined"
          sx={NEUTRAL_BTN}
            ariaLabel="View linked products"
            buttonText={expanded ? 'Hide products' : 'Products'}
            disabled={busy}
            onClick={toggleExpand}
          />
        </Box>
      </Box>

      {expanded && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Spinner color="brand" />
            </Box>
          ) : (
            <>
              {canWrite && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                  <TextField
                    label="Link product (GLPC id)"
                    value={linkDraft}
                    onChange={(e) => setLinkDraft(e.target.value)}
                    size="small"
                    sx={{ width: 240 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    ariaLabel="Link product to recall"
                    buttonText="Link"
                    disabled={busy || !linkDraft.trim()}
                    onClick={() => void linkProduct()}
                  />
                </Box>
              )}

              {activeLinks.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No products linked to this recall.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {activeLinks.map((l) => (
                    <Box
                      key={l.glpProductId}
                      sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          flexShrink: 0,
                          bgcolor: '#e0e0e0',
                          backgroundImage: l.product?.primaryThumbUrl
                            ? `url(${l.product.primaryThumbUrl})`
                            : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {l.product?.itemName ?? l.glpProductId}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {l.glpProductId}
                          {l.product?.brand ? ` · ${l.product.brand}` : ''}
                        </Typography>
                      </Box>
                      {canWrite && (
                        <Button
                          variant="outlined"
                          sx={DANGER_BTN}
                          color="error"
                          ariaLabel={`Unlink ${l.glpProductId}`}
                          buttonText="Unlink"
                          disabled={busy}
                          onClick={() => void unlinkProduct(l.glpProductId)}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  );
}
